import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FilterService, FilterMetadata } from 'primeng/api';
import { Table } from 'primeng/table';
import { Subscription } from 'rxjs';

import { UserDataCache } from '@shared/models';

import { UserCacheService } from './http-client';
import { LocalStorageService } from './local-storage.service';

export interface Filter {
  [filed: string]: FilterMetadata | FilterMetadata[];
}

export interface Column {
  order: number;
  isVisible: boolean;
}

export interface Sort {
  mode?: 'single' | 'multiple';
  field: string;
  order: number;
}

export type TableFiltersSaveMethod = 'local' | 'api' | 'url';

@Injectable()
export class TableFiltersService implements OnDestroy {
  private _columns: { [tableKey: string]: { [fieldKey: string]: Column } } = {};
  private _pageSize: { [tableKey: string]: number } = {};
  private _currentPage: { [tableKey: string]: number } = {};
  private _sort: { [tableKey: string]: Sort } = {};
  private _filters: { [tableKey: string]: Filter } = {};
  private table: Table;
  private componentName: string;
  private autoSaveFilters = false;
  private autoSaveColumns = false;
  private autoSaveSort = false;
  private autoSavePageSize = false;
  private autoSaveCurrentPage = false;
  private saveMethod: TableFiltersSaveMethod = 'local';
  private version = '1.0';
  private subs: Subscription[] = [];

  get filters(): { [key: string]: unknown } {
    return Object.keys(this.table?.filters ?? {}).reduce(
      (arr, item: string) => ({ ...arr, [item]: ((this.table?.filters ?? {})[item] as FilterMetadata)?.value }),
      {}
    );
  }

  constructor(
    private localStorageService: LocalStorageService,
    private filterService: FilterService,
    private userCacheService: UserCacheService,
    private route: ActivatedRoute
  ) {}

  ngOnDestroy(): void {
    this.subs?.forEach((x) => x?.unsubscribe());
  }

  init(
    table: Table,
    componentName: string,
    autoSaveFilters: boolean = true,
    autoSaveColumns: boolean = true,
    autoSaveSort: boolean = true,
    autoSavePageSize: boolean = true,
    autoSaveCurrentPage: boolean = false,
    saveMethod: TableFiltersSaveMethod = 'local',
    version: string = '1.0'
  ) {
    this.table = table;
    this.componentName = componentName;
    this.autoSaveFilters = autoSaveFilters;
    this.autoSaveColumns = autoSaveColumns;
    this.autoSaveSort = autoSaveSort;
    this.autoSavePageSize = autoSavePageSize;
    this.autoSaveCurrentPage = autoSaveCurrentPage;
    this.saveMethod = saveMethod;
    this.version = version;

    setTimeout(() => {
      this.loadPageSize(this.table.rows);
      this.loadCurrentPage();
      this.loadFilters();
      this.loadSort();
      this.loadColumns();
      this.reloadLazyTable();
    }, 0);

    if (this.table) {
      this.subs.push(this.table.onFilter.subscribe(() => this.saveFilter()));
      this.subs.push(this.table.onColReorder.subscribe(() => this.saveColumns()));
      this.subs.push(this.table.onSort.subscribe(() => this.saveSort()));
      this.subs.push(this.table.rowsChange.subscribe((rows) => this.savePageSize(rows)));
      this.subs.push(this.table.onPage.subscribe((event) => this.saveCurrentPage(event.first ?? 0)));
    }
  }

  loadFilters() {
    if (this.autoSaveFilters && this.table) {
      this.loadState<Filter>(LocalStorageService.APP_FILTERS, this._filters, (filters) => {
        this.table.filters = filters ?? {};
      });
    }
  }

  loadSort() {
    if (this.autoSaveSort && this.table) {
      this.loadState<Sort>(LocalStorageService.APP_SORT, this._sort, (sort) => {
        if (sort) {
          this.table.sortOrder = sort.order;
          this.table.sortMode = sort.mode;
          this.table.sortField = sort.field;
        }
      });
    }
  }

  loadColumns() {
    if ((this.autoSaveColumns, this.table)) {
      this.loadState<{ [fieldKey: string]: Column }>(LocalStorageService.APP_COLUMNS, this._columns, (columns) => {
        if (this.table && columns) {
          this.table.columns = this.table?.columns.sort((a, b) => {
            const orderA = columns?.[a.field]?.order ?? 0;
            const orderB = columns?.[b.field]?.order ?? 0;
            return orderA - orderB;
          });
        }
      });
    }
  }

  loadPageSize(defaultPageSize: number) {
    if (this.autoSavePageSize && this.table) {
      this.loadState<number>(LocalStorageService.APP_PAGE_SIZE, this._pageSize, (pageSize) => {
        this.table.rows = pageSize ?? defaultPageSize;
      });
    }
  }

  loadCurrentPage() {
    if (this.autoSaveCurrentPage && this.table) {
      this.loadState<number>(LocalStorageService.APP_CURRENT_PAGE, this._currentPage, (currentPage) => {
        this.table.first = currentPage ?? 0;
      });
    }
  }

  applyFilters(filters: { [s: string]: FilterMetadata | FilterMetadata[] }) {
    this.table.filters = {
      ...this.table.filters,
      ...filters,
    };
    this.table._filter();
    this.saveFilter();
  }

  saveFilter() {
    if (this.autoSaveFilters && this.table) {
      this.saveState(LocalStorageService.APP_FILTERS, this._filters, this.table.filters);
    }
  }

  saveColumns() {
    if (this.autoSaveColumns && this.table) {
      this.saveState(
        LocalStorageService.APP_COLUMNS,
        this._columns,
        this.table.columns.reduce((arr, item, i) => ({ ...arr, [item.field]: { order: i, isVisible: true } }), {})
      );
    }
  }

  saveSort() {
    if (this.autoSaveSort && this.table) {
      this.saveState(LocalStorageService.APP_SORT, this._sort, {
        field: this.table.sortField,
        order: this.table.sortOrder,
        mode: this.table.sortMode,
      });
    }
  }

  savePageSize(pageSize: number) {
    if (this.autoSavePageSize && this.table) {
      this.saveState(LocalStorageService.APP_PAGE_SIZE, this._pageSize, pageSize);
    }
  }

  saveCurrentPage(first: number) {
    if (this.autoSaveCurrentPage && this.table) {
      this.saveState(LocalStorageService.APP_CURRENT_PAGE, this._currentPage, first);
    }
  }

  private reloadLazyTable(): void {
    if (this.table?.lazy) {
      this.table.onLazyLoad.emit(this.table.createLazyLoadMetadata());
    }
  }

  private loadState<T>(type: string, property: { [tableKey: string]: T }, callback: (value: T | null) => void): void {
    if (this.saveMethod === 'url') {
      const value = this.route.snapshot.queryParamMap.get(this.queryParamKey(type));
      callback(value !== null ? (JSON.parse(value) as T) : null);
      return;
    }

    if (this.saveMethod === 'api') {
      this.userCacheService.get(this.componentName, this.componentName, this.componentName, type).subscribe((cache) => {
        const value = cache?.value && cache.version === this.version ? (JSON.parse(cache.value) as T) : null;
        property[this.componentName] = value;
        callback(value);
      });
      return;
    }

    property = this.localStorageService.load(type) ?? {};
    callback(property[this.componentName] ?? null);
  }

  private saveState<T>(type: string, property: { [tableKey: string]: T }, value: T): void {
    if (this.saveMethod === 'url') {
      property[this.componentName] = value;
      const url = new URL(window.location.href);
      url.searchParams.set(this.queryParamKey(type), JSON.stringify(value));
      window.history.replaceState(window.history.state, '', url);
      return;
    }

    if (this.saveMethod === 'api') {
      const model: UserDataCache = {
        entityId: this.componentName,
        type,
        version: this.version,
        view: this.componentName,
        value: JSON.stringify(value),
      };
      this.userCacheService.add(this.componentName, model).subscribe(() => {
        property[this.componentName] = value;
      });
      return;
    }

    property = {
      ...(this.localStorageService.load(type) ?? {}),
      [this.componentName]: value,
    };
    this.localStorageService.store(type, property);
  }

  private queryParamKey(type: string): string {
    return `${this.componentName}.${type}`;
  }
}
